import { SessionStorage } from 'app/storage/session.storage';
import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MeetingFormService } from 'app/form/meeting-form/meeting-form.service';
import { Question } from '../form-composer.component';
import { fieldElementId } from 'app/shared/forms/form-validation.util';

export interface Fields{
    [key:string]:string[]
}

@Component({
  selector: 'tick-box',
  template: `
    <div class="box__tick" *ngFor="let question of getKeys()" [id]="getFieldId(question)" [class.box__tick--invalid]="submitted && isInvalid(question)">
        <label class="tick-question-label">
          {{training ? 'Trainings' : question}}<span *ngIf="(type === 'report' || type === 'survey') && required" class="req-mark">*</span>
        </label>
        <div *ngIf="description" class="tick-question-description" [innerHTML]="description"></div>
        <form [formGroup]="form" class="ans" [class.ans--rate]="rate">
            <div *ngFor="let answer of getField(question)" class="ans-item" [class.ans-item--rate]="rate">
                <input
                    formArrayName="{{question}}"
                    (change)="onCheckboxChange($event, question)"
                    [value]="answer" [checked]="isChosen(answer, question)"
                    class="inp-cbx" id="{{question+answer}}" type="checkbox"/>
                <label class="cbx" for="{{question+answer}}">
                    <span class="cbx-box">
                        <svg width="12px" height="10px"><use xlink:href="#check"></use></svg>
                    </span>
                    <span class="cbx-text">{{answer}}</span>
                </label>
                <input *ngIf="isOtherOption(answer) && isChosen(answer, question)"
                       type="text"
                       class="other-specify-input"
                       [formControlName]="getOtherControlName(question)"
                       placeholder="Please specify…">
            </div>
        </form>
    </div>
    <svg class="inline-svg">
        <symbol id="check" viewBox="0 0 12 10">
            <polyline points="1.5 6 4.5 9 10.5 1"></polyline>
        </symbol>
    </svg>
  `,
  styles: [`
    .box__tick {
      padding: 12px 14px;
      background: #fafcfe;
      border: 1px solid #e4e8ee;
      border-radius: 6px;
      margin-bottom: 12px;
    }
    .box__tick--invalid {
      border-color: var(--form-error, #e74c3c);
      background: var(--form-error-bg, #fdecea);
    }
    .req-mark {
      color: var(--form-error, #e74c3c);
      font-weight: 700;
      margin-left: 3px;
    }
    .tick-question-label {
      display: block;
      font-size: 13px;
      font-weight: 700;
      color: #222;
      margin-bottom: 6px;
      line-height: 1.4;
    }
    .tick-question-description {
      font-size: 12px;
      color: #666;
      margin-bottom: 10px;
      line-height: 1.5;
    }
    .ans {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .ans--rate { gap: 4px; }
    .ans-item { flex: 1 1 100%; }
    .ans-item--rate { flex: 1 1 18%; min-width: 70px; }

    .other-specify-input {
      width: 100%;
      margin: 4px 0 4px 26px;
      padding: 7px 10px;
      font-size: 13px;
      color: #333;
      border: 1px solid #d0dce8;
      border-radius: 4px;
      background: #fff;
      max-width: calc(100% - 26px);
    }
    .other-specify-input:focus {
      outline: none;
      border-color: #004E9A;
      box-shadow: 0 0 0 2px rgba(0,78,154,.12);
    }

    .cbx {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      padding: 6px 10px;
      border-radius: 4px;
      transition: background .15s;
      font-size: 13px;
      color: #333;
      margin: 0;
    }
    .cbx:hover { background: #eaf1ff; }

    .cbx-box {
      position: relative;
      width: 18px;
      height: 18px;
      flex-shrink: 0;
      border-radius: 4px;
      border: 1px solid #cccfdb;
      background: #fff;
      transition: border-color .15s, background .15s;
      box-shadow: 0 1px 1px rgba(0,16,75,0.05);
    }
    .cbx-box svg {
      position: absolute;
      top: 3px;
      left: 2px;
      fill: none;
      stroke: #fff;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-dasharray: 16px;
      stroke-dashoffset: 16px;
      transition: stroke-dashoffset .3s ease .1s;
    }

    .cbx:hover .cbx-box { border-color: #004E9A; }

    .inp-cbx {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }
    .inp-cbx:checked + .cbx .cbx-box {
      background: #004E9A;
      border-color: #004E9A;
    }
    .inp-cbx:checked + .cbx .cbx-box svg { stroke-dashoffset: 0; }

    .inline-svg {
      position: absolute;
      width: 0;
      height: 0;
      pointer-events: none;
      user-select: none;
    }

    @media (max-width: 640px) {
      .ans-item--rate { flex: 1 1 48%; }
    }
  `]
})
export class TickBoxComponent implements OnInit, AfterViewInit {

    @Input()
        fields: Question = {
            'Question 1': ['A', 'B', 'C'],
            'Question 2': ['A', 'B', 'C', 'E', 'F']
        }

    @Input()
        type: string = ""

    @Input()
        description: string[] |string = ""

    @Input()
        required: boolean = true

    @Input()
        submitted: boolean = false

    unique: boolean = false
    rate: boolean = false
    training: boolean = false

    meetingsOrder = [
        'first', 'second', 'third', 'fourth', "final"
    ]

    MEETING_FORM = 0

    form : FormGroup = this.fb.group({})

    constructor(
        private formService: MeetingFormService,
        private fb: FormBuilder,
        private auth: SessionStorage
    ) {}

    ngAfterViewInit(): void {
        let question = Object.values(this.fields)[0]
        let first = Object.values(question)[0]
        if(first === 'type_radio'){
            this.unique = true

            var question_2 = Object.values(this.fields)[0]
            var second = Object.values(question_2)[1]
            if(second === 'rate'){
                this.rate = true
            }
        } else if (first === 'trainings'){
            this.training = true
        }
    }

   getKeys(): string[]{
    return Object.keys(this.fields).filter( x => x != "description" && x != "section" && x != "section_description")
   }

   getFieldId(question: string): string {
    return fieldElementId(question)
   }

   isInvalid(question: string): boolean {
    return !!this.form.get(question)?.invalid
   }

   getField(key: string): string[]{
    
    if(this.unique){
        if(this.rate){
            return Object.values(this.fields[key]).filter( x => x != 'type_radio').filter( x => x != 'rate')
        }
        else{
            return Object.values(this.fields[key]).filter( x => x != 'type_radio')
        }
    } else if (this.training){
        return Object.values(this.fields[key]).filter( x => x != 'trainings')
    }
    return Object.values(this.fields[key])
   }

  ngOnInit(): void {
    
    switch(this.type){
        case "survey":
            if(this.auth.isProject()){
                this.form = this.formService.getProjectSurveyForm()   
            }
            else{
                this.form = this.formService.getEpeSurveyForm()   
            }
            break
        case "report":
            this.form = this.formService.getReportForm()   
            break
        case "advice_project_survey":
            this.form = this.formService.getProjectAdviceForm()   
            break
        case "project_survey_meeting":
            this.form = this.formService.getProjectSurveyMeetingForm()
            break
        default:
            this.form = this.formService.getForm()
    }

    this.ensureOtherControls()
  }

  isOtherOption(answer: string): boolean {
    return /\(please specify\)/i.test(answer)
  }

  getOtherControlName(question: string): string {
    return `${question} - Other`
  }

  private ensureOtherControls(): void {
    for (const question of this.getKeys()) {
      for (const answer of this.getField(question)) {
        if (this.isOtherOption(answer)) {
          const name = this.getOtherControlName(question)
          if (!this.form.contains(name)) {
            this.form.addControl(name, new FormControl(''))
          }
        }
      }
    }
  }

  onCheckboxChange(event: Event, question: string): void {

    const target = event.target as HTMLInputElement;
    const selectedAnswers = (this.form.controls[question] as FormArray)
    if(this.unique)
        selectedAnswers.clear()
    if(!target)
        return
    if(target.checked)
        selectedAnswers.push(new FormControl(target.value))
    else{
        const index = selectedAnswers.controls.findIndex(x => x.value === target.value);
        selectedAnswers.removeAt(index);
    }        
  }

  isChosen(answer: string, question: string): boolean{

    const selectedAnswers = (this.form.controls[question] as FormArray)

    return selectedAnswers.controls.findIndex(x => x.value === answer) > -1

  }

  


}
